<template>
  <div>
    <b-alert v-if="!setting.subscription" show variant="warning" class="mb-0">
      ยังโหลดข้อมูลแพ็กเกจ AI Assistant ไม่ได้ กรุณาโหลดหน้าใหม่อีกครั้ง
    </b-alert>
    <template v-else>
      <div class="mb-3">
        <div class="d-flex flex-wrap align-items-center">
          <b-badge variant="primary" class="mr-2 px-2 py-1">{{ packageLabel }}</b-badge>
          <b-badge v-if="setting.subscription.active" variant="success" class="mr-2 px-2 py-1">
            แพ็กเกจใช้งานได้
          </b-badge>
          <small v-if="setting.subscription.endDt" class="text-muted">
            ระยะเวลา {{ formatDate(setting.subscription.startDt) }} – {{ formatDate(setting.subscription.endDt) }}
          </small>
        </div>
        <small v-if="isFree" class="text-muted d-block mt-2">
          อัปเกรดเป็น STARTER หรือ PRO ได้ โดยติดต่อทีม DentCloud
        </small>
        <b-alert v-if="!setting.subscription.active" show variant="warning" class="mt-2 mb-0">
          แพ็กเกจ AI Assistant ของคลินิกถูกระงับหรือหมดอายุ จึงยังเปิดใช้งานไม่ได้ กรุณาติดต่อทีม DentCloud
        </b-alert>
      </div>

      <div class="mb-3">
        <div class="d-flex flex-wrap justify-content-between">
          <span>การใช้งานเดือนนี้ ({{ usage ? usage.month : "-" }})</span>
          <span v-if="usage && usage.used !== null">
            {{ formatNumber(usage.used) }} / {{ formatNumber(usage.quotaLimit) }} ครั้ง
          </span>
          <span v-else class="text-muted">ยังอ่านจำนวนการใช้งานไม่ได้ในขณะนี้</span>
        </div>
        <b-progress v-if="usage && usage.used !== null" :max="100" height="1rem" class="mt-1">
          <b-progress-bar :value="usage.usedPercent" :variant="usageVariant" />
        </b-progress>
        <small v-if="usage" class="text-muted d-block mt-1">
          รีเซ็ตวันที่ {{ formatDate(usage.resetsAt) }} · บันทึกการเรียกใช้เดือนนี้
          {{ formatNumber(usage.log.totalCalls) }} ครั้ง (นับโควตา {{ formatNumber(usage.log.meteredCalls) }} ครั้ง)
        </small>
      </div>

      <div class="d-flex align-items-center">
        <b-form-checkbox
          :key="toggleKey"
          :checked="setting.enabled"
          switch
          size="lg"
          :disabled="isSaving"
          @change="onToggle"
        >
          {{ setting.enabled ? "เปิดใช้งาน AI Assistant อยู่" : "ปิดใช้งาน AI Assistant อยู่" }}
        </b-form-checkbox>
        <b-spinner v-if="isSaving" small class="ml-2" />
      </div>
      <div class="mt-2">
        <small v-if="setting.dpa.current" class="text-muted">
          ยอมรับข้อตกลง DPA ฉบับ {{ setting.dpa.acceptedVersion }} โดย {{ acceptedByLabel }}
          เมื่อ {{ formatDateTime(setting.dpa.acceptedAt) }}
        </small>
        <small v-else class="text-danger">
          ยังไม่ได้ยอมรับข้อตกลง DPA ฉบับปัจจุบัน ({{ setting.dpa.currentVersion }})
        </small>
      </div>
      <b-alert v-if="errorMessage" show variant="danger" class="mt-3 mb-0">{{ errorMessage }}</b-alert>
    </template>
    <McpDpaModal ref="dpaModal" :dpa="setting.dpa" :clinic-name="setting.clinic.name" @accepted="onDpaAccepted" />
  </div>
</template>

<script>
import moment from "moment";
import McpDpaModal from "@/components/mcp/McpDpaModal.vue";
import { mcpErrorMessage } from "@/components/mcp/mcpErrors";
import { mcpTierLabel } from "@/components/mcp/mcpLabels";

export default {
  name: "McpPackageStatus",
  components: { McpDpaModal },
  props: {
    clinicUrl: { type: String, required: true },
    setting: { type: Object, required: true },
    usage: { type: Object, default: null },
  },
  data() {
    return { isSaving: false, errorMessage: "", toggleKey: 0 };
  },
  computed: {
    isFree() {
      return this.setting.subscription.tier === "FREE";
    },
    packageLabel() {
      const { tier, quotaLimit } = this.setting.subscription;
      if (tier === "FREE") {
        const limit = quotaLimit === null || quotaLimit === undefined ? "-" : this.formatNumber(quotaLimit);
        return `แพ็กเกจ: ${mcpTierLabel(tier)} (${limit} ครั้งต่อเดือน)`;
      }
      return `แพ็กเกจ ${mcpTierLabel(tier)}`;
    },
    usageVariant() {
      const percent = this.usage && this.usage.usedPercent ? this.usage.usedPercent : 0;
      if (percent >= 90) return "danger";
      if (percent >= 80) return "warning";
      return "success";
    },
    acceptedByLabel() {
      const by = this.setting.dpa.acceptedBy;
      if (!by) return "-";
      return by.name || by.email || `uid ${by.uid}`;
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "-";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },
    formatNumber(value) {
      return value === null || value === undefined ? "-" : Number(value).toLocaleString("th-TH");
    },
    onToggle(checked) {
      this.errorMessage = "";
      if (checked && !this.setting.dpa.current) {
        // Re-render the switch in its saved (off) position; it turns on only after DPA + enable succeed.
        this.toggleKey += 1;
        this.$refs.dpaModal.show();
        return;
      }
      this.saveEnabled(checked);
    },
    async onDpaAccepted() {
      this.isSaving = true;
      this.errorMessage = "";
      try {
        await this.$store.dispatch("moduleMcpSetting/acceptDpa", {
          clinicUrl: this.clinicUrl,
          version: this.setting.dpa.currentVersion,
        });
        const updated = await this.$store.dispatch("moduleMcpSetting/updateEnabled", {
          clinicUrl: this.clinicUrl,
          enabled: true,
        });
        this.$emit("changed", updated);
      } catch (error) {
        this.errorMessage = mcpErrorMessage(error);
        this.toggleKey += 1;
        // The DPA may already be stored even if enabling failed (e.g. suspended package).
        await this.refresh();
      } finally {
        this.isSaving = false;
      }
    },
    async saveEnabled(enabled) {
      this.isSaving = true;
      try {
        const updated = await this.$store.dispatch("moduleMcpSetting/updateEnabled", {
          clinicUrl: this.clinicUrl,
          enabled,
        });
        this.$emit("changed", updated);
      } catch (error) {
        this.errorMessage = mcpErrorMessage(error);
        this.toggleKey += 1;
      } finally {
        this.isSaving = false;
      }
    },
    async refresh() {
      try {
        const setting = await this.$store.dispatch("moduleMcpSetting/fetchSetting", { clinicUrl: this.clinicUrl });
        this.$emit("changed", setting);
      } catch (error) {
        console.error("McpPackageStatus: refresh failed", error);
      }
    },
  },
};
</script>
